import { create } from 'zustand'

import {
	clientGetStreamingServiceTrackInformation,
	clientPromptSearch,
	clientSearchByAudio,
} from '@/client'

let prefetchAudioUrlInFlight: string | null = null

/** Prevents duplicate concurrent prompt searches for the same text (e.g. UI + URL parse). */
const promptSearchInFlightTexts = new Set<string>()

/** Prevents duplicate concurrent setSearchByAudio poll loops for the same URL (e.g. UI + URL parse). */
const audioSearchWaitInFlightUrls = new Set<string>()

const normalizeAiSearchResults = (
	results: AiSearchResult[] | null | undefined
): AiSearchResult[] =>
	(results ?? [])
		.map((result) => ({
			...result,
			id: Number(result.id),
		}))
		.filter((result) => Number.isFinite(result.id))

const toAlgoliaIdFilters = (results: AiSearchResult[]): string =>
	results.map((result) => `Id = ${result.id}`).join(' OR ')

interface PrefetchedSearchByAudio {
	service: string | null
	title: string | null
	url: string | null
	aiSearchResults: AiSearchResult[] | null | undefined
	aiSearchFilters: string | null | undefined
}

interface AiSearchState {
	aiSearchResults: AiSearchResult[] | null
	aiSearchFilters: string | null
	blockAiSearch: boolean
	clearAiSearch: () => void
	aiSearchHasFailed: boolean
	aiSearchPending: boolean
	setAiSearchPending: (boolean: boolean) => void

	prefetchedSearchByAudio: PrefetchedSearchByAudio | null
	searchByAudio: {
		service: string
		title: string
		url: string
	} | null
	prefetchSearchByAudio: (url: string, isLoggedIn: boolean) => void
	removePrefetchedSearchByAudio: () => void
	setSearchByAudio: (url?: string) => void

	searchByPrompt: string | null
	setSearchByPrompt: (text: string) => void
}

const useAiSearchStore = create<AiSearchState>()((set, get) => ({
	aiSearchFilters: null,
	aiSearchResults: null,
	blockAiSearch: false,
	aiSearchHasFailed: false,
	clearAiSearch: () => {
		audioSearchWaitInFlightUrls.clear()
		set(() => ({
			aiSearchFilters: null,
			aiSearchResults: null,
			searchByAudio: null,
			searchByPrompt: null,
			prefetchedSearchByAudio: null,
			aiSearchHasFailed: false,
		}))
	},

	aiSearchPending: false,
	setAiSearchPending: (boolean) => {
		set(() => ({
			aiSearchPending: boolean,
		}))
	},

	prefetchedSearchByAudio: null,
	searchByAudio: null,
	prefetchSearchByAudio: async (url, isLoggedIn) => {
		if (url === get().searchByAudio?.url) {
			return
		}

		if (url === get().prefetchedSearchByAudio?.url) {
			return
		}

		if (prefetchAudioUrlInFlight === url) {
			return
		}

		prefetchAudioUrlInFlight = url

		try {
			get().clearAiSearch()

			const { service, title } =
				(await clientGetStreamingServiceTrackInformation(url)) ?? {}

			if (service && title) {
				set(() => ({
					blockAiSearch: !isLoggedIn,
					prefetchedSearchByAudio: {
						service,
						title,
						url,
						aiSearchResults: [],
						aiSearchFilters: '',
					},
				}))

				if (!isLoggedIn) {
					return
				}

				const results = normalizeAiSearchResults(await clientSearchByAudio(url))

				set((state) => ({
					prefetchedSearchByAudio: {
						...state.prefetchedSearchByAudio,
						aiSearchResults: results,
						aiSearchFilters: toAlgoliaIdFilters(results),
					},
				}))
			}
		} finally {
			if (prefetchAudioUrlInFlight === url) {
				prefetchAudioUrlInFlight = null
			}
		}
	},
	removePrefetchedSearchByAudio: () => {
		set(() => ({
			prefetchedSearchByAudio: {
				service: null,
				title: null,
				url: null,
				aiSearchResults: null,
				aiSearchFilters: null,
			},
		}))
	},
	setSearchByAudio: async (input) => {
		if (input === undefined) {
			return
		}

		if (audioSearchWaitInFlightUrls.has(input)) {
			return
		}
		audioSearchWaitInFlightUrls.add(input)

		set(() => ({
			aiSearchPending: true,
		}))

		const waitForPrefetch = setInterval(() => {
			const { blockAiSearch, prefetchedSearchByAudio } = get()

			if (
				(input === prefetchedSearchByAudio?.url ||
					input === prefetchedSearchByAudio?.title) &&
				(prefetchedSearchByAudio?.aiSearchFilters || blockAiSearch)
			) {
				clearInterval(waitForPrefetch)
				audioSearchWaitInFlightUrls.delete(input)

				set(() => ({
					searchByAudio: {
						service: prefetchedSearchByAudio.service,
						title: prefetchedSearchByAudio.title,
						url: prefetchedSearchByAudio.url,
					},
					aiSearchFilters: prefetchedSearchByAudio.aiSearchFilters,
					aiSearchResults: prefetchedSearchByAudio.aiSearchResults ?? null,
					aiSearchPending: false,
				}))
			}
		}, 250)
	},

	searchByPrompt: null,
	setSearchByPrompt: async (text) => {
		if (text === get().searchByPrompt) {
			return
		}

		//Dont prompt search urls
		if (text.match(/(http|https|www)/i) !== null) {
			//Give time for algolia to start searching, else no results pops up
			setTimeout(() => {
				set(() => ({
					aiSearchPending: false,
				}))
			})
			return
		}

		if (promptSearchInFlightTexts.has(text)) {
			return
		}
		promptSearchInFlightTexts.add(text)
		try {
			get().clearAiSearch()

			set(() => ({
				aiSearchPending: true,
				aiSearchHasFailed: false,
			}))

			set(() => ({
				searchByPrompt: text,
			}))

			const results = normalizeAiSearchResults(await clientPromptSearch(text))

			if (!results.length) {
				set(() => ({
					aiSearchResults: null,
					aiSearchFilters: '',
					blockAiSearch: false,
					aiSearchHasFailed: true,
					aiSearchPending: false,
				}))

				return
			}

			set(() => ({
				aiSearchResults: results,
				aiSearchFilters: toAlgoliaIdFilters(results),
				blockAiSearch: false,
			}))

			//Give time for algolia to start searching, else no results pops up
			setTimeout(() => {
				set(() => ({
					aiSearchPending: false,
				}))
			})
		} finally {
			promptSearchInFlightTexts.delete(text)
		}
	},
}))

export default useAiSearchStore
